import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { BadgeComponent } from '../badge/badge.component';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[tabName]',
  standalone: true,
})
export class TabDirective {
  template = inject<TemplateRef<any>>(TemplateRef);

  @Input() tabName = '';
  @Input() count?: any;
  @Input() dynamicTabButtonCssStyle?: Record<string, any>;
}

@Component({
  selector: 'app-dynamic-tabs',
  templateUrl: './dynamic-tabs.component.html',
  styleUrls: ['./dynamic-tabs.component.scss'],
  imports: [NgTemplateOutlet, BadgeComponent, NgStyle],
})
export class DynamicTabsComponent implements AfterContentInit, AfterViewInit, OnChanges {
  @ContentChildren(TabDirective) templates!: QueryList<TabDirective>;
  /**
   * The active tab to be selected
   */
  @Input() activeTab?: string;
  /**
   * Tabs to be displayed in the tab bar if no templates are provided
   */
  @Input() tabs: { label: string; displayName?: string; routerLink?: string; count?: number }[] = [];
  /**
   * Whether the tabs should be disabled or not
   */
  @Input() disabled = false;
  /**
   * Event emitter for when a tab is selected
   */
  @Output() tabSelected = new EventEmitter<string>();
  selectedTemplate: TabDirective | undefined;
  activeTabIndex = -1; // Initialize as no tab is being selected to force a selection on the first tab
  // ← NEW: grab all rendered <button #tabButton> elements
  @ViewChildren('tabButton', { read: ElementRef })
  tabButtons!: QueryList<ElementRef>;
  // ← NEW: properties bound to the indicator's style
  indicatorLeft = 0;
  indicatorWidth = 0;
  private readonly _router = inject(Router);
  private readonly _cdr = inject(ChangeDetectorRef);

  ngAfterContentInit() {
    this._setupTabs();
  }

  ngAfterViewInit() {
    // Ensure the initial tab selection and content are updated after view initialization
    this._cdr.detectChanges();
    setTimeout(() => {
      this._animateIndicator();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['activeTab'] && !changes['activeTab'].isFirstChange()) {
      this._setupTabs();
      // Wait a tick for the new activeTabIndex to render, then animate:
      setTimeout(() => this._animateIndicator(), 0);
    }
  }

  selectTab(index: number) {
    if (this.activeTabIndex === index) {
      return; // Do nothing if the current tab is reselected
    }

    this.activeTabIndex = index;
    if (this.tabs.length > 0 && this.tabs[index]) {
      const selectedTab = this.tabs[index];
      if (selectedTab.routerLink) {
        this._router.navigateByUrl(selectedTab.routerLink);
      } else {
        this.tabSelected.emit(selectedTab.label);
      }
    } else if (this.templates.toArray()[index]) {
      // Check if using template-based tabs
      this.selectedTemplate = this.templates.toArray()[index];
      this.tabSelected.emit(this.selectedTemplate?.tabName);
    }

    // ← After changing activeTabIndex, slide the indicator
    this._animateIndicator();
  }

  private _setupTabs() {
    let activeIndex = -1;

    // Check if tabs are passed through the input and set the activeIndex accordingly
    if (this.tabs.length > 0) {
      // Try to find the active tab index in the tabs array
      activeIndex = this.tabs.findIndex((tab) => tab.label === this.activeTab);

      // If no activeTab matches or activeTab is not provided, default to the first tab
      if (activeIndex === -1) {
        activeIndex = 0; // Default to the first tab
        this.activeTab = this.tabs[0].label; // Update activeTab to reflect this selection
      }
    } else if (this.templates.length > 0) {
      // If no tabs input provided, fall back to using content children
      activeIndex = this.templates.toArray().findIndex((t) => t.tabName === this.activeTab);

      // Default to the first tab if no activeTab is provided or it doesn't match
      if (activeIndex === -1) {
        activeIndex = 0;
      }
    }

    // Select the tab based on the determined activeIndex
    if (activeIndex !== -1) {
      this.selectTab(activeIndex);
    }
  }

  /**
   * Measure the currently active <button> and update indicatorLeft/indicatorWidth
   */
  private _animateIndicator() {
    const buttonsArray = this.tabButtons?.toArray() || [];
    if (!buttonsArray.length || this.activeTabIndex < 0) {
      return;
    }
    const activeBtnEl = buttonsArray[this.activeTabIndex].nativeElement as HTMLElement;
    this.indicatorLeft = activeBtnEl.offsetLeft;
    this.indicatorWidth = activeBtnEl.offsetWidth;
  }
}
